import React, { useCallback, useMemo, useRef, useState } from 'react';
import { withStyles } from '@mui/styles';

import Dialog from '@mui/material/Dialog';
import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useLangFile } from 'js/context/LanguageContext';
import PrescriptionSettings from './PrescriptionSettings';
import { usePrescriptionJob } from 'js/components/Prescription/PrescriptionJobContext';
import PropTypes from 'prop-types';
import { MetaTypes } from '../../PrescriptionJob';
import Box from '@mui/material/Box';
import { Button, TrackedSelectSeasonFormControl } from '../../../Analytics/TrackedComponents';
import { ANALYTICS_EVENTS } from '../../../Analytics/AnalyticsConstants';
import {
  PRESCRIPTION_METERING,
  PRESCRIPTION_UNIT,
} from '../../../../constants/PrescriptionConstants';
import { red } from '@mui/material/colors';
import { SURVEY_LAYERS } from '../../../../constants/SurveyLayers';
import { SQUARE_METER_COVERSION_VALUE } from '../../PrescriptionUtils';
import { Identifier } from '../../../../model/prescription/PrescriptionModelReference';
import { useSurveyForField } from '../../useSurveyForField';
import {
  getLatestPrescriptionModelStrategy,
  getPrescriptionStrategyFromRef,
} from '../../../../model/prescription/PrescriptionModelsAndVersions';
import { useFeatureFlags } from '../../../../featureFlags/FeatureFlagContext';
import { NEW_SEEDING_MODEL_FLAG } from '../../../../featureFlags/featureFlags';

const styles = (theme) => ({
  content: {
    overflowY: 'visible',
  },
  actions: {
    justifyContent: 'space-between',
  },
  '@media (max-width: 1024px), (max-height: 800px)': {
    root: {
      padding: theme.spacing(1),
    },
    content: {
      padding: theme.spacing(1),
    },
    title: {
      padding: theme.spacing(1),
      textAlign: 'center',
    },
    actions: {
      padding: theme.spacing(1),
      margin: 0,
    },
  },
  warning: {
    color: red['A700'],
  },
});

const PrescriptionSettingsDialog = ({
  dispatch,
  classes,
  open,
  onSetOpen,
  onExit,
  exitEnabled,
  disableDialog,
  initialLayerType,
  isBulkPrescription,
  handleSetPrefix,
  bulkPrescriptionFields,
  surveys,
  initializePrescriptionJob,
}) => {
  const LangFile = useLangFile();
  const { prescriptionJob, setPrescriptionJob } = usePrescriptionJob();
  const { getSurveyForFieldAndLayer } = useSurveyForField();
  const { isEnabled } = useFeatureFlags();
  const newSeedingModelEnabled = isEnabled(NEW_SEEDING_MODEL_FLAG);
  const [useManualLimeEfficiencyInput, setUseManualLimeEfficiencyInput] = useState(false);
  const seedingCalcRef = useRef();
  const isAdjustingBasedOnSoilAnalysisEnabled = useMemo(() => {
    if (prescriptionJob?.prescriptionModel) {
      return prescriptionJob.prescriptionModel.identifier === Identifier.MultiLayerSeedingModel;
    }

    const fieldIds = isBulkPrescription
      ? bulkPrescriptionFields.map((field) => field.fieldId)
      : [prescriptionJob.fieldId];

    return (
      fieldIds.length > 0 &&
      fieldIds.every((fieldId) => !!getSurveyForFieldAndLayer(fieldId, SURVEY_LAYERS.CLAY))
    );
  }, [prescriptionJob, bulkPrescriptionFields, isBulkPrescription]);

  const initiallyAdjustPrescriptionBasedOnSoilAnalysis = prescriptionJob.maxPrescription
    ? prescriptionJob.prescriptionModel?.identifier === Identifier.MultiLayerSeedingModel
    : isAdjustingBasedOnSoilAnalysisEnabled;
  const [adjustPrescriptionBasedOnSoilAnalysis, setAdjustPrescriptionBasedOnSoilAnalysis] =
    useState(
      prescriptionJob.metaType === MetaTypes.SEEDING &&
        initiallyAdjustPrescriptionBasedOnSoilAnalysis
    );
  const initialCalculationSettings = useRef({
    prescriptionPerHectare: prescriptionJob.maxPrescription / prescriptionJob.fieldSize,
    metaType: prescriptionJob.metaType,
    adjustPrescriptionBasedOnSoilAnalysis:
      prescriptionJob.metaType === MetaTypes.SEEDING &&
      initiallyAdjustPrescriptionBasedOnSoilAnalysis,
    hasIntervals: !!prescriptionJob.intervals?.length,
  });
  const [maxPrescription, setMaxPrescription] = useState(() => {
    if (prescriptionJob.metering === PRESCRIPTION_METERING.HA) {
      return prescriptionJob.maxPrescription / prescriptionJob.fieldSize;
    }
    if (prescriptionJob.metering === PRESCRIPTION_METERING.SQUARE_METER) {
      return (
        prescriptionJob.maxPrescription / (prescriptionJob.fieldSize * SQUARE_METER_COVERSION_VALUE)
      );
    }
    return prescriptionJob.maxPrescription;
  });

  const updateMaxPrescription = useCallback(
    (updatedMaxPrescription) => {
      setMaxPrescription(updatedMaxPrescription);
    },
    [setMaxPrescription]
  );

  const handleAdjustPrescriptionBasedOnSoilAnalysis = useCallback(
    async (shouldAdjust) => {
      setAdjustPrescriptionBasedOnSoilAnalysis(shouldAdjust);
    },
    [setAdjustPrescriptionBasedOnSoilAnalysis]
  );

  const updateSettingRef = useRef({});
  const updateSetting = (key) => {
    if (!updateSettingRef.current[key]) {
      updateSettingRef.current[key] = (value) => {
        setPrescriptionJob({ [key]: value });
      };
    }
    return updateSettingRef.current[key];
  };

  const updateMetatype = useCallback(
    (value) => {
      const limeInfo =
        prescriptionJob.limeInfo && prescriptionJob.limeInfo.tolerance
          ? prescriptionJob.limeInfo
          : { tolerance: 1 };

      const intervals = prescriptionJob.intervals
        ? prescriptionJob.intervals.map((interval) => ({
            ...interval,
            prescription: 0,
          }))
        : undefined;

      // Since Lime Demand has to be a pseudo layer for the appliction to handle things without a major refactor
      // We have to change the layer when doing a Lime job, and vice versa
      const metaTypeToChanges = {
        [MetaTypes.FERTILIZING]: { metaType: value, layer: initialLayerType },
        [MetaTypes.SEEDING]: { metaType: value, layer: initialLayerType },
        [MetaTypes.LIME]: { metaType: value, limeInfo, layer: SURVEY_LAYERS.RT },
        [MetaTypes.SPRAYING]: { metaType: value, layer: initialLayerType },
        [MetaTypes.SPOT_SPRAYING]: { metaType: value, intervals, layer: initialLayerType },
        [MetaTypes.LEGACY]: { metaType: value, layer: initialLayerType },
      };
      const changes = metaTypeToChanges[value];
      setAdjustPrescriptionBasedOnSoilAnalysis(
        value === MetaTypes.SEEDING && isAdjustingBasedOnSoilAnalysisEnabled
      );
      setPrescriptionJob(changes);
    },
    [prescriptionJob, isAdjustingBasedOnSoilAnalysisEnabled]
  );

  const getPrescriptionPerUnit = useCallback(
    (updatedMetering) => {
      const fieldSize = prescriptionJob.fieldSize;

      if (prescriptionJob.metaType === MetaTypes.LIME) {
        return maxPrescription;
      }

      switch (updatedMetering) {
        case PRESCRIPTION_METERING.HA:
          if (prescriptionJob.metering === PRESCRIPTION_METERING.SQUARE_METER) {
            return maxPrescription * SQUARE_METER_COVERSION_VALUE;
          }
          return maxPrescription / fieldSize;

        case PRESCRIPTION_METERING.SQUARE_METER:
          if (prescriptionJob.metering === PRESCRIPTION_METERING.HA) {
            return maxPrescription / SQUARE_METER_COVERSION_VALUE;
          }

          return maxPrescription / (fieldSize * SQUARE_METER_COVERSION_VALUE);

        case PRESCRIPTION_METERING.TOTAL:
          if (prescriptionJob.metering === PRESCRIPTION_METERING.SQUARE_METER) {
            return maxPrescription * fieldSize * SQUARE_METER_COVERSION_VALUE;
          }
          return maxPrescription * fieldSize;
        default:
          return maxPrescription;
      }
    },
    [prescriptionJob.metaType, prescriptionJob.fieldSize, maxPrescription, prescriptionJob.metering]
  );

  const updateUnit = useCallback(
    (updatedUnit) => {
      if (updatedUnit === prescriptionJob.unit) return;

      if (prescriptionJob.metering === PRESCRIPTION_METERING.TOTAL) {
        setPrescriptionJob({ unit: updatedUnit });
        return;
      }

      if (updatedUnit === PRESCRIPTION_UNIT.PIECES_M2) {
        setPrescriptionJob({ unit: updatedUnit, metering: PRESCRIPTION_METERING.SQUARE_METER });
        setMaxPrescription(getPrescriptionPerUnit(PRESCRIPTION_METERING.SQUARE_METER));
        return;
      }

      setPrescriptionJob({
        unit: updatedUnit,
        metering: PRESCRIPTION_METERING.HA,
      });
      setMaxPrescription(getPrescriptionPerUnit(PRESCRIPTION_METERING.HA));
    },
    [prescriptionJob.metering, prescriptionJob.unit, getPrescriptionPerUnit]
  );

  const updateLimeInfo = useCallback(
    (value) => {
      setPrescriptionJob({ limeInfo: { ...prescriptionJob.limeInfo, ...value } });
    },
    [prescriptionJob]
  );

  const [errors, setErrors] = useState({});

  const errorsIncludes = (...keysToInclude: string[]): boolean => {
    return Object.keys(errors)
      .filter((key) => {
        return keysToInclude.includes(key);
      })
      .some((key) => errors[key]);
  };

  const defaultErrorStrategy = (): boolean => {
    const hasErrors = errorsIncludes('jobName', 'maxPrescription');
    const isMissingValues = prescriptionJob.metaType === '' || prescriptionJob.unit === '';
    return hasErrors || isMissingValues;
  };

  const limeErrorStrategy = (): boolean => {
    const limeEfficiencySelector = useManualLimeEfficiencyInput
      ? 'manualLimeEfficiency'
      : 'limeSupplierEfficiency';
    const hasError = errorsIncludes('jobName', 'maxPrescription', limeEfficiencySelector);
    const isMissingValues =
      prescriptionJob.metaType === '' ||
      prescriptionJob.unit === '' ||
      !prescriptionJob.limeInfo?.tolerance ||
      !prescriptionJob.limeInfo?.efficiency; // This last one handles if efficiency is chosen from dropdown

    return hasError || isMissingValues;
  };

  // Select controls doesn't seem to play well with errors, resulting in us needing to check for values instead of errors
  const disabledStategy = {
    [MetaTypes.FERTILIZING]: () => defaultErrorStrategy(),
    [MetaTypes.SEEDING]: () => defaultErrorStrategy(),
    [MetaTypes.LIME]: () => limeErrorStrategy(),
    [MetaTypes.SPRAYING]: () => defaultErrorStrategy(),
    [MetaTypes.SPOT_SPRAYING]: () => defaultErrorStrategy(),
    [MetaTypes.LEGACY]: () => defaultErrorStrategy(),
    '': () => true,
  };

  // If there are any errors, disable the submit button
  const disabled = () => disabledStategy[prescriptionJob.metaType]();

  const convertMaxPrescriptionBasedOnUnit = useCallback(
    (maxPrescription) => {
      const fieldSize = prescriptionJob.fieldSize;
      switch (prescriptionJob.metering) {
        case PRESCRIPTION_METERING.HA:
          return maxPrescription * fieldSize;
        case PRESCRIPTION_METERING.SQUARE_METER:
          return maxPrescription * (fieldSize * SQUARE_METER_COVERSION_VALUE);
        default:
          return maxPrescription;
      }
    },
    [prescriptionJob.metering, prescriptionJob.fieldSize]
  );

  const handleOnClose = useCallback(() => {
    handleSetPrefix(prescriptionJob.jobName);
    onSetOpen(false);

    if (prescriptionJob.metaType === MetaTypes.SEEDING) {
      seedingCalcRef.current?.saveSeedingCalcParamsToStore();
    }

    const convertedMaxPrescription = convertMaxPrescriptionBasedOnUnit(maxPrescription);
    const initialSettings = initialCalculationSettings.current;
    const prescriptionPerHectare = convertedMaxPrescription / prescriptionJob.fieldSize;
    const prescriptionChanged =
      Math.abs(prescriptionPerHectare - initialSettings.prescriptionPerHectare) >
      Number.EPSILON *
        4 *
        Math.max(
          1,
          Math.abs(prescriptionPerHectare),
          Math.abs(initialSettings.prescriptionPerHectare)
        );
    const modelChanged =
      adjustPrescriptionBasedOnSoilAnalysis !==
      initialSettings.adjustPrescriptionBasedOnSoilAnalysis;
    const shouldInitialize =
      !initialSettings.hasIntervals ||
      prescriptionChanged ||
      modelChanged ||
      prescriptionJob.metaType !== initialSettings.metaType;
    let prescriptionStrategy = null;

    if (adjustPrescriptionBasedOnSoilAnalysis && newSeedingModelEnabled) {
      if (prescriptionJob.prescriptionModel) {
        prescriptionStrategy = getPrescriptionStrategyFromRef(prescriptionJob.prescriptionModel);
      } else {
        prescriptionStrategy = getLatestPrescriptionModelStrategy(
          Identifier.MultiLayerSeedingModel
        );
      }

      if (modelChanged) {
        setPrescriptionJob({ prescriptionModel: prescriptionStrategy.toReference() });
      }
    } else if (modelChanged) {
      setPrescriptionJob({ prescriptionModel: null });
    }

    if (prescriptionChanged || !initialSettings.hasIntervals) {
      updateSetting('maxPrescription')(convertedMaxPrescription);
    }
    if (shouldInitialize) {
      initializePrescriptionJob(convertedMaxPrescription, prescriptionStrategy);
    }
  }, [
    prescriptionJob.jobName,
    prescriptionJob.metaType,
    prescriptionJob.fieldSize,
    prescriptionJob.prescriptionModel,
    adjustPrescriptionBasedOnSoilAnalysis,
    newSeedingModelEnabled,
    maxPrescription,
    convertMaxPrescriptionBasedOnUnit,
    initializePrescriptionJob,
    onSetOpen,
  ]);

  const updateMetering = useCallback(
    (updatedMetering) => {
      if (prescriptionJob.metering === updatedMetering) return;

      setMaxPrescription(getPrescriptionPerUnit(updatedMetering));

      updateSetting('metering')(updatedMetering);
    },
    [getPrescriptionPerUnit, prescriptionJob.metering]
  );

  const handleOnError = useCallback((key, hasError) => {
    setErrors((current) => ({ ...current, [key]: hasError }));
  }, []);

  const metering = useMemo(() => {
    return prescriptionJob.metaType === MetaTypes.LIME
      ? PRESCRIPTION_METERING.HA
      : prescriptionJob.metering;
  }, [prescriptionJob.metaType, prescriptionJob.metering]);

  const SeasonControl = (
    <Box
      style={disableDialog ? { pointerEvents: 'none', opacity: '0.4' } : {}}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
      }}
    >
      <span>{LangFile.PrescriptionSettingsDialog.title}</span>
      <TrackedSelectSeasonFormControl
        {...ANALYTICS_EVENTS.selector('prescription_season')}
        value={prescriptionJob.seasonId}
        onChange={updateSetting('seasonId')}
        fieldId={prescriptionJob.fieldId}
        disable={disableDialog}
      />
    </Box>
  );

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={'lg'}
      className={classes.root}
      data-testid="prescription-settings-dialog"
    >
      <DialogTitle className={classes.title}>
        {disableDialog && (
          <div className={classes.warning}>{LangFile.PrescriptionSettingsDialog.missingField}</div>
        )}
        {!disableDialog && SeasonControl}
      </DialogTitle>
      <DialogContent className={classes.content} style={disableDialog ? { opacity: '0.4' } : {}}>
        <PrescriptionSettings
          maxPrescription={maxPrescription}
          metering={metering}
          fieldSize={prescriptionJob.fieldSize}
          unit={prescriptionJob.unit}
          jobName={prescriptionJob.jobName}
          metaType={prescriptionJob.metaType}
          limeInfo={prescriptionJob.limeInfo}
          onError={handleOnError}
          onUpdateMetering={updateMetering}
          onUpdateMaxPrescription={updateMaxPrescription}
          onUpdateJobName={updateSetting('jobName')}
          onUpdateUnit={updateUnit}
          onUpdateMetaType={updateMetatype}
          onUpdateLimeInfo={updateLimeInfo}
          isDisabled={disableDialog}
          useManualLimeEfficiencyInput={useManualLimeEfficiencyInput}
          onUpdateUseManualEfficiencyLimeInput={setUseManualLimeEfficiencyInput}
          assets={prescriptionJob.assets}
          isBulkPrescription={isBulkPrescription}
          bulkPrescriptionFields={bulkPrescriptionFields}
          surveys={surveys}
          ref={seedingCalcRef}
          dispatch={dispatch}
          adjustPrescriptionBasedOnSoilAnalysis={adjustPrescriptionBasedOnSoilAnalysis}
          setAdjustPrescriptionBasedOnSoilAnalysis={handleAdjustPrescriptionBasedOnSoilAnalysis}
        />
      </DialogContent>
      <DialogActions className={classes.actions}>
        {exitEnabled ? (
          <Button
            onClick={onExit}
            variant={'outlined'}
            {...ANALYTICS_EVENTS.button('prescription_settings_exit')}
          >
            {LangFile.PrescriptionSettingsDialog.exit}
          </Button>
        ) : (
          <span />
        )}
        {!disableDialog && (
          <Button
            {...ANALYTICS_EVENTS.button('prescription_settings_done')}
            data-testid="prescription-settings-done"
            onClick={handleOnClose}
            color="primary"
            disabled={disabled()}
            variant={'contained'}
          >
            {LangFile.PrescriptionSettingsDialog.done}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

PrescriptionSettingsDialog.propTypes = {
  open: PropTypes.bool,
  exitEnabled: PropTypes.bool,
  onSetOpen: PropTypes.func,
  onExit: PropTypes.func,
  disableDialog: PropTypes.bool,
  initialLayerType: PropTypes.string,
  isBulkPrescription: PropTypes.bool,
  handleSetPrefix: PropTypes.func,
  bulkPrescriptionFields: PropTypes.array,
  surveys: PropTypes.array,
};

export default withStyles(styles)(PrescriptionSettingsDialog);
